import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";

export type OrgTreeRow = {
    node: organisation_tree_node;
    depth: number;
    hasChildren: boolean;
};

export const canWrite = (node?: organisation_tree_node) => (node?.permission_type ?? 0) >= 4;

export const ACCESS_LEVELS = [
    { id: 7, get name() { return appState.t("objects.access.full"); } },
    { id: 3, get name() { return appState.t("objects.access.read"); } },
    { id: 1, get name() { return appState.t("objects.access.use"); } },
];

export const accessLabel = (permissionType: number) => {
    if (permissionType >= 4) return appState.t("objects.access.full");
    if (permissionType >= 2) return appState.t("objects.access.read");
    return appState.t("objects.access.use");
};

/**
 * The organisation to draw this one under, or null if it is a root of the view.
 *
 * A root marks itself either by having no parent or by being its own parent, and
 * production uses the latter. A parent we cannot reach is not something we can
 * draw under either, so the view starts where the user's permissions start.
 */
const parentOf = (
    node: organisation_tree_node,
    reachable: Set<number>,
): number | null =>
    node.parent_organisation !== null &&
        node.parent_organisation !== node.id &&
        reachable.has(node.parent_organisation)
        ? node.parent_organisation
        : null;

const childrenByParent = (nodes: organisation_tree_node[]) => {
    const reachable = new Set(nodes.map((node) => node.id));
    const byParent = new Map<number | null, organisation_tree_node[]>();

    for (const node of nodes) {
        const parent = parentOf(node, reachable);
        byParent.set(parent, [...(byParent.get(parent) ?? []), node]);
    }

    return byParent;
};

/**
 * Depth-first list of rows to render. Collapsed subtrees are dropped, and while a
 * search is active every ancestor of a match stays visible so the path to it reads
 * as a path rather than a flat result list.
 */
export const flattenOrgTree = (
    nodes: organisation_tree_node[],
    { expanded, search }: { expanded: Set<number>; search: string },
): OrgTreeRow[] => {
    const byParent = childrenByParent(nodes);
    const needle = search.trim().toLowerCase();

    const onPathToMatch = new Set<number>();
    if (needle) {
        const mark = (node: organisation_tree_node): boolean => {
            const children = byParent.get(node.id) ?? [];
            const childMatched = children.map(mark).filter(Boolean).length > 0;
            const matched = node.name.toLowerCase().includes(needle) || childMatched;
            if (matched) onPathToMatch.add(node.id);
            return matched;
        };
        (byParent.get(null) ?? []).forEach(mark);
    }

    const rows: OrgTreeRow[] = [];
    const walk = (node: organisation_tree_node, depth: number) => {
        const children = (byParent.get(node.id) ?? []).filter(
            (child) => !needle || onPathToMatch.has(child.id),
        );
        rows.push({ node, depth, hasChildren: children.length > 0 });
        if (needle || expanded.has(node.id)) children.forEach((child) => walk(child, depth + 1));
    };

    (byParent.get(null) ?? [])
        .filter((node) => !needle || onPathToMatch.has(node.id))
        .forEach((node) => walk(node, 0));

    return rows;
};

/** Every organisation id from the root down to `orgId`, so the tree can reveal it. */
export const ancestorsOf = (nodes: organisation_tree_node[], orgId: number): number[] => {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const reachable = new Set(byId.keys());
    const path: number[] = [];
    const seen = new Set<number>([orgId]);

    let node = byId.get(orgId);
    let current = node ? parentOf(node, reachable) : null;

    // `seen` stops any parent cycle in the data from spinning here forever.
    while (current !== null && !seen.has(current)) {
        seen.add(current);
        path.push(current);
        node = byId.get(current);
        current = node ? parentOf(node, reachable) : null;
    }

    return path;
};
